// File: client/src/variants/royale/shrink.js
//
// Pure shrink logic for Chess Royale. No React, no engine coupling — takes/returns
// plain board arrays and numbers so it is exhaustively unit-testable.
//
// The playable area is a centred square that collapses over time:
//   ring 0 -> full 8x8   (cols/rows 0..7)
//   ring 1 -> 6x6        (2..5) after the 1st shrink
//   ring 2 -> 4x4        (2..3? no — centred: 2..5 shrinks to 3..4)
// We centre the square so the shrink is symmetric. For an 8x8 board the centred
// squares of size 8,6,4,2 occupy the inclusive index ranges:
//   size 8 -> [0,7]   size 6 -> [1,6]   size 4 -> [2,5]   size 2 -> [3,4]
//
// A shrink happens every SHRINK_EVERY full turns (both players have moved). Turn
// counting is done by the runtime; this module is a pure function of the count.

export const SHRINK_EVERY = 5; // full turns between shrinks
const SIZES = [8, 6, 4, 2];    // successive playable sizes

/**
 * How many shrinks have occurred after `fullTurns` completed full turns.
 * Clamped so the board never goes below 2x2.
 * @param {number} fullTurns
 * @returns {number} 0..3
 */
export function shrinkLevel(fullTurns) {
  const level = Math.floor(fullTurns / SHRINK_EVERY);
  return Math.min(level, SIZES.length - 1);
}

/**
 * The current playable size (8,6,4,2) after `fullTurns`.
 */
export function playableSize(fullTurns) {
  return SIZES[shrinkLevel(fullTurns)];
}

/**
 * The inclusive [min,max] index range of the centred playable square for a size.
 * @param {number} size one of 8,6,4,2
 * @returns {{min:number,max:number}}
 */
export function playableRange(size) {
  const margin = (8 - size) / 2;
  return { min: margin, max: 7 - margin };
}

/**
 * Is cell (r,c) inside the playable area at the given fullTurns count?
 */
export function isPlayable(r, c, fullTurns) {
  const { min, max } = playableRange(playableSize(fullTurns));
  return r >= min && r <= max && c >= min && c <= max;
}

/**
 * Return the list of cells that are destroyed (outside the playable square) at a
 * given fullTurns count. Useful for UI highlighting and testing.
 * @returns {{row:number,col:number}[]}
 */
export function destroyedCells(fullTurns) {
  const cells = [];
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (!isPlayable(r, c, fullTurns)) cells.push({ row: r, col: c });
    }
  }
  return cells;
}

/**
 * Apply destruction to a board (mutates in place): any piece standing outside the
 * playable area is removed. Returns the list of pieces destroyed (char + pos) so
 * the caller can detect a king loss and report it.
 * @param {string[][]} board
 * @param {number} fullTurns
 * @returns {{piece:string,row:number,col:number}[]}
 */
export function applyShrink(board, fullTurns) {
  const destroyed = [];
  const { min, max } = playableRange(playableSize(fullTurns));
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const inside = r >= min && r <= max && c >= min && c <= max;
      if (!inside && board[r][c]) {
        destroyed.push({ piece: board[r][c], row: r, col: c });
        board[r][c] = '';
      }
    }
  }
  return destroyed;
}

/**
 * Given the board after a shrink, determine a victory status if a king was removed.
 * - both kings gone (shouldn't normally happen the same shrink, but handle it): draw
 * - white king gone -> black wins
 * - black king gone -> white wins
 * - both present -> null (no royale victory)
 * @param {string[][]} board
 * @returns {{over:boolean,result:string,winner:string|null}|null}
 */
export function kingElimination(board) {
  let whiteKing = false;
  let blackKing = false;
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (board[r][c] === 'K') whiteKing = true;
      else if (board[r][c] === 'k') blackKing = true;
    }
  }
  if (!whiteKing && !blackKing) return { over: true, result: 'draw', winner: null };
  if (!whiteKing) return { over: true, result: 'royale', winner: 'black' };
  if (!blackKing) return { over: true, result: 'royale', winner: 'white' };
  return null;
}
